import { Button, Dialog } from "@mui/material";
import React, { useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import useUploadProductFile from "../../../hooks/product/useUploadProductFile";
import AsyncSelect from "../inputs/AsyncSelect";
import toast from "react-hot-toast";

const ProductExcelUploadModal = ({
	isOpen,
	handleClose,
	initialFile = null,
	initialBrandId = null,
	uploadId = null,
}) => {
  const {
    brandOptions,
    brandHasMore,
    fetchBrands,
    brandQuery,
    brandId,
    handleBrandSelect,
    uploadProductFile,
	previewExcelFile,
	previewProductFile,
	uploadRequest,
    downloadTemplate,
  } = useUploadProductFile({
    onClose: handleClose,
  });

  const uploadNormalRef = useRef(null);
	const [fileToUpload, setFileToUpload] = useState(initialFile);
	const [fileName, setFileName] = useState(initialFile?.name || "");
	const [mapping, setMapping] = useState({ sheet_one: {}, sheet_two: {} });
	const preview = previewProductFile.data?.sheets;
	const selectedBrandId = brandId || initialBrandId;

  // Get user role from sessionStorage
  const userRole = sessionStorage.getItem("roles");
  const isAdmin = userRole === "admin";

  const handleFileChange = (e, uploadId) => {
    const file = e.target.files[0];
    if (file) {
	  previewProductFile.reset();
      setFileName(file.name);
      setFileToUpload(file);
	  setMapping({ sheet_one: {}, sheet_two: {} });
    } else {
      setFileName("");
      setFileToUpload(null);
    }
  };

  const handleReviewFile = () => {
    if (!isAdmin && !selectedBrandId) {
      toast.error("Please select a brand");
      return;
    }
    if (!fileToUpload) {
      toast.error("Please select a file to upload");
      return;
    }
	previewExcelFile(fileToUpload);
  };

	React.useEffect(() => {
		if (!preview) return;
		const initialMapping = {};
		["sheet_one", "sheet_two"].forEach((sheetKey) => {
			initialMapping[sheetKey] = {};
			(preview[sheetKey]?.fields || []).forEach((field) => {
				initialMapping[sheetKey][field.target] = field.suggested_source || "";
			});
		});
		setMapping(initialMapping);
	}, [preview]);

	const handleImport = () => {
		for (const sheetKey of ["sheet_one", "sheet_two"]) {
			const sheet = preview?.[sheetKey];
			if (!sheet || sheet.sources.length === 0) continue;
			const missing = sheet.fields.filter(
				(field) => field.required && !mapping[sheetKey]?.[field.target]
			);
			if (missing.length > 0) {
				toast.error(`Map all required fields in ${sheetKey === "sheet_one" ? "Products" : "Variations"}.`);
				return;
			}
			const selected = Object.values(mapping[sheetKey] || {}).filter(Boolean);
			if (new Set(selected).size !== selected.length) {
				toast.error(`The same Excel column cannot be mapped twice in ${sheetKey === "sheet_one" ? "Products" : "Variations"}.`);
				return;
			}
		}
		uploadProductFile(fileToUpload, selectedBrandId, mapping, uploadId);
	};

	const renderMappingTable = (sheetKey, title) => {
		const sheet = preview?.[sheetKey];
		if (!sheet || sheet.sources.length === 0) return null;
		return (
			<div className="mt-5">
				<h2 className="font-semibold mb-2">{title}</h2>
				<div className="max-h-72 overflow-auto border rounded">
					<table className="w-full text-sm">
						<thead className="bg-gray-100 sticky top-0">
							<tr><th className="p-2 text-left">Product field</th><th className="p-2 text-left">Excel column</th></tr>
						</thead>
						<tbody>
							{sheet.fields.map((field) => (
								<tr key={field.target} className="border-t">
									<td className="p-2">{field.target}{field.required && <span className="text-red-500"> *</span>}</td>
									<td className="p-2">
										<select
											className="input w-full"
											value={mapping[sheetKey]?.[field.target] || ""}
											onChange={(event) => setMapping((current) => ({
												...current,
												[sheetKey]: { ...current[sheetKey], [field.target]: event.target.value },
											}))}
										>
											<option value="">Do not import</option>
											{sheet.sources.map((source) => <option key={source} value={source}>{source}</option>)}
										</select>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

  const handleDownloadTemplate = () => {
	downloadTemplate(selectedBrandId);
  };

  return (
    <Dialog maxWidth={preview ? "lg" : "sm"} fullWidth open={isOpen} onClose={handleClose}>
      <div className="p-5">
        <h1 className="text-lg font-semibold">Upload Products Excel</h1>

        <div className="flex justify-end">
          <button
            className="underline underline-offset-1"
            onClick={handleDownloadTemplate}
          >
            Download template
          </button>
        </div>

        <div>
          <label className="mb-2 block text-sm mt-5">
            Select Brand:{!isAdmin && <span className="text-red-500">*</span>}
          </label>
          <AsyncSelect
            options={brandOptions}
            hasMore={brandHasMore}
            fetchNext={fetchBrands}
            isLoading={brandQuery?.isFetching}
            value={selectedBrandId}
            onChange={handleBrandSelect}
          />
        </div>

        <button
          className="flex gap-2 items-center mt-5 underline underline-offset-2"
          type="submit"
          onClick={() => uploadNormalRef.current.click()}
        >
          <FiUpload />
          Select File
        </button>
        <input
          ref={uploadNormalRef}
          onChange={(e) => handleFileChange(e, null)}
          type="file"
          accept=".xls, .xlsx"
          id="fileInput"
          className="hidden"
        />
        {fileName && <p className="mt-5">{fileName}</p>}

		{preview && (
			<>
				<p className="mt-5 text-sm text-gray-600">Review the detected columns. Required product fields are marked with *.</p>
				{renderMappingTable("sheet_one", "Products sheet")}
				{renderMappingTable("sheet_two", "Variations sheet")}
			</>
		)}

        <div className="grid place-items-center mt-5">
          <Button
            variant="contained"
            className={" primaryBtn-solid"}
			disabled={previewProductFile.isLoading || uploadRequest.isLoading}
			onClick={preview ? handleImport : handleReviewFile}
          >
			{previewProductFile.isLoading
				? "Reading Excel..."
				: uploadRequest.isLoading
				  ? "Importing..."
				  : preview
					? "Confirm Mapping & Import"
					: "Review Column Mapping"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ProductExcelUploadModal;
